import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
} from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toPlainText } from '../../utils/toPlainText';
import { stableJsonKey } from './richTextBlocks';

const imgKey = (img, prefix) =>
  img.url || img.documentId || img.id || stableJsonKey(prefix, img);

const thumbAlt = (img) =>
  toPlainText(img.alt) || toPlainText(img.alternativeText) || 'Foto logbook';

/**
 * @param {{
 *   sections: { key: string; heading: string | null; images: unknown[] }[];
 *   useStripOnMobile: boolean;
 * }} props
 */
const LogbookUnifiedGallery = ({ sections, useStripOnMobile }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const flatImages = useMemo(() => {
    const out = [];
    sections.forEach((sec) => {
      const imgs = Array.isArray(sec.images) ? sec.images : [];
      imgs.forEach((img) => {
        if (img && img.url) out.push(img);
      });
    });
    return out;
  }, [sections]);

  const openLightbox = useCallback(
    (img) => {
      const idx = flatImages.findIndex(
        (x) => imgKey(x, 'flat') === imgKey(img, 'flat')
      );
      if (idx < 0) return;
      setLightboxIndex(idx);
      setLightboxOpen(true);
    },
    [flatImages]
  );

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  const goPrev = useCallback(() => {
    setLightboxIndex((i) => (i - 1 + flatImages.length) % flatImages.length);
  }, [flatImages.length]);

  const goNext = useCallback(() => {
    setLightboxIndex((i) => (i + 1) % flatImages.length);
  }, [flatImages.length]);

  useEffect(() => {
    if (!lightboxOpen || flatImages.length === 0) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, flatImages.length, goPrev, goNext]);

  const renderThumb = useCallback(
    (img, keyPrefix, layout) => (
      <Image
        key={imgKey(img, keyPrefix)}
        src={img.url}
        alt={thumbAlt(img)}
        cursor="pointer"
        role="button"
        tabIndex={0}
        aria-label="Apri immagine a schermo intero"
        onClick={() => openLightbox(img)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openLightbox(img);
          }
        }}
        borderRadius={layout.borderRadius}
        flexShrink={layout.flexShrink}
        w={layout.w}
        h={layout.h}
        objectFit={layout.objectFit}
      />
    ),
    [openLightbox]
  );

  if (!sections.length) return null;

  const current = flatImages[lightboxIndex];
  const showNav = flatImages.length > 1;

  return (
    <Box as="section" aria-label="Galleria immagini" mt={10}>
      {sections.map((sec) => (
        <Box key={sec.key} mb={sec.heading ? 6 : 0}>
          {toPlainText(sec.heading) ? (
            <Heading
              as="h2"
              size="sm"
              color="#3b235a"
              mb={3}
              fontWeight="semibold"
            >
              {toPlainText(sec.heading)}
            </Heading>
          ) : null}
          {useStripOnMobile ? (
            <>
              <HStack
                display={{ base: 'flex', md: 'none' }}
                spacing={3}
                overflowX="auto"
                py={1}
                sx={{ WebkitOverflowScrolling: 'touch' }}
              >
                {sec.images.map((img) =>
                  renderThumb(img, 'strip', {
                    borderRadius: 'lg',
                    flexShrink: 0,
                    w: '200px',
                    h: '140px',
                    objectFit: 'cover',
                  })
                )}
              </HStack>
              <SimpleGrid
                display={{ base: 'none', md: 'grid' }}
                columns={{ md: 2, lg: 3 }}
                spacing={3}
              >
                {sec.images.map((img) =>
                  renderThumb(img, 'grid', {
                    borderRadius: 'md',
                    flexShrink: undefined,
                    w: '100%',
                    h: 'auto',
                    objectFit: 'cover',
                  })
                )}
              </SimpleGrid>
            </>
          ) : (
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
              {sec.images.map((img) =>
                renderThumb(img, 'gal', {
                  borderRadius: 'md',
                  flexShrink: undefined,
                  w: '100%',
                  h: undefined,
                  objectFit: undefined,
                })
              )}
            </SimpleGrid>
          )}
        </Box>
      ))}

      <Modal
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        size="full"
        motionPreset="none"
      >
        <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(4px)" />
        <ModalContent
          m={0}
          maxW="100vw"
          minH="100vh"
          borderRadius={0}
          bg="transparent"
          boxShadow="none"
        >
          <ModalCloseButton
            color="white"
            size="lg"
            top={4}
            right={4}
            zIndex={2}
            bg="blackAlpha.600"
            _hover={{ bg: 'blackAlpha.800' }}
            rounded="full"
            aria-label="Chiudi"
          />
          <ModalBody
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={{ base: 2, md: 6 }}
            minH="100vh"
            position="relative"
          >
            {current?.url ? (
              <Image
                src={current.url}
                alt={thumbAlt(current)}
                maxH="calc(100vh - 2rem)"
                maxW="100%"
                w="auto"
                h="auto"
                objectFit="contain"
                mx="auto"
              />
            ) : null}
            {showNav ? (
              <>
                <IconButton
                  aria-label="Immagine precedente"
                  icon={<FaChevronLeft />}
                  position="absolute"
                  left={{ base: 2, md: 4 }}
                  top="50%"
                  transform="translateY(-50%)"
                  size="lg"
                  rounded="full"
                  color="white"
                  bg="blackAlpha.600"
                  _hover={{ bg: 'blackAlpha.800' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    goPrev();
                  }}
                  zIndex={2}
                />
                <IconButton
                  aria-label="Immagine successiva"
                  icon={<FaChevronRight />}
                  position="absolute"
                  right={{ base: 2, md: 4 }}
                  top="50%"
                  transform="translateY(-50%)"
                  size="lg"
                  rounded="full"
                  color="white"
                  bg="blackAlpha.600"
                  _hover={{ bg: 'blackAlpha.800' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    goNext();
                  }}
                  zIndex={2}
                />
              </>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LogbookUnifiedGallery;
